import React, { useCallback, useEffect, useRef, useState } from "react";
import { useToast } from "../context/ToastContext";
import "./ToolPage.css";
import flaskApi from "../services/flaskApi";
import { extractFilenameFromHeader } from "../utils/fileUtils";
import { extractErrorMessage } from "../utils/errorUtils";
import ToolInfoModal from "../components/common/ToolInfoModal";
import ToolFileUploadField from "./shared/ToolFileUploadField";
import ToolPageLayout from "./shared/ToolPageLayout";
import ToolProgressPanel from "./shared/ToolProgressPanel";
import ToolJobsSection from "./shared/ToolJobsSection";
import ToolConfirmModal from "./shared/ToolConfirmModal";
import JobInputActions from "./shared/JobInputActions";
import useJobsPagination from "./shared/useJobsPagination";


const STATUS_SCHEDULED = "scheduled";
const STATUS_DONE = "done";
const STATUS_FAILED = "failed";
const STATUS_CANCELLED = "cancelled";

const RUN_NOW = "now";
const RUN_SCHEDULE = "schedule";

const ENDPOINT = "/shopify/create-products";

// Pre-selected channel. Mirrors what the Matrixify import does today: new
// products land on Point of Sale, while the Online Store is driven per row by
// the sheet's own Published column rather than by this picker.
const DEFAULT_CHANNEL_NAME = "Point of Sale";

// Must match PRODUCT_STATUSES in the backend's product_creator.py.
const STATUS_OPTIONS = [
  { value: "Active", label: "Active" },
  { value: "Draft", label: "Draft" },
  { value: "Archived", label: "Archived" },
];

const STATUS_HELPER = {
  Active:
    "Rows with a blank Status column become Active. They still only appear on the channels selected above.",
  Draft:
    "Rows with a blank Status column become Draft — useful for staging a season before it goes live.",
  Archived:
    "Rows with a blank Status column become Archived. Rarely what you want for a new build.",
};

// "YYYY-MM-DDTHH:mm" in the user's local time — what <input type="datetime-local"> expects.
const formatLocalDatetimeInput = (date) => {
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
};

const formatScheduledDisplay = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};


function CreateProductsTool() {
  const [inputFile, setInputFile] = useState(null);
  const [sheetName, setSheetName] = useState("");
  const [channels, setChannels] = useState([]);
  const [channelsLoading, setChannelsLoading] = useState(true);
  const [channelsError, setChannelsError] = useState("");
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [defaultStatus, setDefaultStatus] = useState("Active");
  const [dryRun, setDryRun] = useState(true);
  const [abortOnError, setAbortOnError] = useState(false);
  const [runTime, setRunTime] = useState(RUN_NOW);
  const [scheduledAt, setScheduledAt] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [downloadingJobId, setDownloadingJobId] = useState(null);
  const [cancellingJobId, setCancellingJobId] = useState(null);
  const [downloadingInputJobId, setDownloadingInputJobId] = useState(null);
  const [restartingJobId, setRestartingJobId] = useState(null);
  const { addToast } = useToast();
  const [showInfoModal, setShowInfoModal] = useState(false);
  // Set to { scheduledIso } while the live-run confirmation is on screen.
  const [pendingLiveRun, setPendingLiveRun] = useState(null);

  const minScheduledAt = formatLocalDatetimeInput(new Date());

  const {
    jobs,
    page,
    totalPages,
    totalFinished,
    setPage,
    refetch: refetchJobs,
  } = useJobsPagination({
    endpoint: `${ENDPOINT}/jobs`,
    cacheKey: "createProducts.jobs",
    pageSize: 10,
  });

  // Publication ids are opaque Shopify GIDs, so the list has to come from the
  // backend rather than being hard-coded here.
  const loadChannels = useCallback(async () => {
    setChannelsLoading(true);
    setChannelsError("");
    try {
      const response = await flaskApi.get("/shopify/publication-channels");
      const list = response.data?.channels || [];
      setChannels(list);
      const preselect = list.find((c) => c.name === DEFAULT_CHANNEL_NAME);
      setSelectedChannels(preselect ? [preselect.id] : []);
    } catch (error) {
      const message = await extractErrorMessage(
        error,
        "Failed to load sales channels."
      );
      setChannelsError(message);
    } finally {
      setChannelsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadChannels();
  }, [loadChannels]);

  const toggleChannel = (id) => {
    setSelectedChannels((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]
    );
  };

  const selectedChannelNames = channels
    .filter((c) => selectedChannels.includes(c.id))
    .map((c) => c.name);

  const toolInfo = {
    toolName: "Create Products",
    description:
      "Build new Shopify products from a Matrixify-style product sheet — one row per variant, grouped by Handle. The SKU Builder output works as-is. Validates the whole file first and reports every problem by row. Runs as a background job.",
    steps: [
      "Upload the product sheet. Any Matrixify Products-format file works; the SKU Builder's output needs no changes.",
      "Pick the sales channels new products should go on. Point of Sale is pre-selected; the Online Store is driven per row by the sheet's own Published column.",
      "Choose what Status rows get when their Status column is blank.",
      "Leave Dry run ticked for the first pass. It validates everything — including whether each handle and SKU is already taken in Shopify — without creating anything.",
      "Open the report and fix anything on the Issues sheet, which names the row, column and value for every problem.",
      "Re-run with Dry run unticked to create the products.",
    ],
    templates: [],
  };

  const guideTips = [
    "The dry run is a real rehearsal, not just a file check. It runs the same validation as the live run, including the two checks that need Shopify — is this handle already taken, is this SKU already on another product. A clean dry run means the live run will work.",
    "Rows are grouped by Handle. Product-level columns (Title, Vendor, Type, Tags, Status, Published) are read from the group's first row; if a later row disagrees you get a warning and the first row wins.",
    "Errors block one product, not the whole file — everything else is still created. Tick 'Stop if any row has an error' if you'd rather fix the file and re-run than get a partial build.",
    "The report has three sheets: Products (what was created or would be), Issues (every error and warning, with the spreadsheet row number), and Summary (totals and the run's settings).",
    "This tool only creates. A handle that already exists in Shopify is reported as an error, never updated — rename the handle or edit the existing product in the Shopify admin.",
    "Inventory columns must be named 'Inventory Available: <Location>' and metafields 'Metafield: namespace.key [type]'. An unknown location name stops the whole run rather than silently dropping the quantities.",
    "Scheduled jobs are persisted server-side and survive deploys. A handle that was free when you scheduled the job can be taken by the time it runs, so the checks are repeated at run time.",
  ];

  const previousStatusRef = useRef({});
  useEffect(() => {
    const previous = previousStatusRef.current;
    jobs.forEach((j) => {
      const prev = previous[j.job_id];
      if (prev && prev !== j.status) {
        if (j.status === STATUS_DONE) {
          addToast(
            `Create-products job ${j.job_id} complete. Report is ready.`,
            "success"
          );
        } else if (j.status === STATUS_FAILED) {
          addToast(
            `Create-products job ${j.job_id} failed: ${j.error || "unknown error"}`,
            "error"
          );
        } else if (j.status === STATUS_CANCELLED) {
          addToast(`Create-products job ${j.job_id} cancelled.`, "info");
        }
      }
    });
    previousStatusRef.current = Object.fromEntries(
      jobs.map((j) => [j.job_id, j.status])
    );
  }, [jobs, addToast]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!inputFile) {
      addToast("Please upload a product sheet.", "error");
      return;
    }
    const maxSize = 100 * 1024 * 1024;
    if (inputFile.size > maxSize) {
      addToast("File is too large. Maximum size is 100MB.", "error");
      return;
    }

    let scheduledIso = null;
    if (runTime === RUN_SCHEDULE) {
      if (!scheduledAt) {
        addToast("Pick a date and time to schedule the job.", "error");
        return;
      }
      const target = new Date(scheduledAt);
      if (Number.isNaN(target.getTime())) {
        addToast("Scheduled date/time is not valid.", "error");
        return;
      }
      if (target.getTime() <= Date.now() + 30 * 1000) {
        addToast(
          "Scheduled date/time must be at least 30 seconds in the future.",
          "error"
        );
        return;
      }
      scheduledIso = target.toISOString();
    }

    // A live run creates real products, so make it an explicit choice.
    if (!dryRun) {
      setPendingLiveRun({ scheduledIso });
      return;
    }

    await startJob(scheduledIso);
  };

  const startJob = async (scheduledIso) => {
    const formData = new FormData();
    formData.append("input_file", inputFile);
    formData.append("dry_run", dryRun ? "true" : "false");
    formData.append("default_status", defaultStatus);
    if (selectedChannels.length) {
      formData.append("publications", selectedChannels.join(","));
    }
    if (abortOnError) {
      formData.append("abort_on_error", "true");
    }
    const trimmedSheet = sheetName.trim();
    if (trimmedSheet) {
      formData.append("sheet_name", trimmedSheet);
    }
    if (scheduledIso) {
      formData.append("scheduled_at", scheduledIso);
    }

    setUploading(true);
    setUploadProgress(0);
    try {
      const response = await flaskApi.post(ENDPOINT, formData, {
        onUploadProgress: (evt) => {
          const pct = Math.round((evt.loaded * 100) / (evt.total || 1));
          setUploadProgress(pct);
        },
      });
      const jobId = response.data?.job_id;
      if (!jobId) {
        addToast("Server did not return a job ID.", "error");
        return;
      }
      const modeWord = dryRun ? "Dry-run" : "Live";
      if (scheduledIso) {
        addToast(
          `${modeWord} create-products job ${jobId} scheduled for ${formatScheduledDisplay(
            scheduledIso
          )}.`,
          "info"
        );
      } else {
        addToast(
          `${modeWord} create-products job ${jobId} queued. Tracking progress…`,
          "info"
        );
      }
      if (page !== 1) await setPage(1);
      else await refetchJobs();
      setInputFile(null);
      setScheduledAt("");
      setRunTime(RUN_NOW);
    } catch (error) {
      const message = await extractErrorMessage(
        error,
        "Failed to start create-products job."
      );
      addToast(message, "error");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const confirmLiveRun = async () => {
    const pending = pendingLiveRun;
    setPendingLiveRun(null);
    if (!pending) return;
    await startJob(pending.scheduledIso);
  };

  const handleCancel = async (job) => {
    if (!job?.job_id) return;
    const ok = window.confirm(
      `Cancel scheduled create-products job ${job.job_id}? This can't be undone.`
    );
    if (!ok) return;
    setCancellingJobId(job.job_id);
    try {
      await flaskApi.delete(`${ENDPOINT}/jobs/${job.job_id}`);
      addToast(`Job ${job.job_id} cancelled.`, "success");
      await refetchJobs();
    } catch (error) {
      const message = await extractErrorMessage(error, "Failed to cancel job.");
      addToast(message, "error");
    } finally {
      setCancellingJobId(null);
    }
  };

  const handleDownload = async (job) => {
    if (!job?.job_id) return;
    setDownloadingJobId(job.job_id);
    try {
      const response = await flaskApi.get(
        `${ENDPOINT}/jobs/${job.job_id}/download`,
        { responseType: "blob" }
      );
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const contentDisposition =
        response.headers["content-disposition"] ||
        response.headers["Content-Disposition"];
      const extractedFilename = extractFilenameFromHeader(contentDisposition);
      link.setAttribute(
        "download",
        extractedFilename || job.result_filename || "create_products_report.xlsx"
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      addToast("Report downloaded.", "success");
    } catch (error) {
      const message = await extractErrorMessage(
        error,
        "Failed to download report."
      );
      addToast(message, "error");
    } finally {
      setDownloadingJobId(null);
    }
  };

  const handleDownloadInput = async (job) => {
    if (!job?.job_id) return;
    setDownloadingInputJobId(job.job_id);
    try {
      const response = await flaskApi.get(
        `${ENDPOINT}/jobs/${job.job_id}/input`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const contentDisposition =
        response.headers["content-disposition"] ||
        response.headers["Content-Disposition"];
      const extractedFilename = extractFilenameFromHeader(contentDisposition);
      link.setAttribute(
        "download",
        extractedFilename || job.input_filename || "input_file"
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      addToast("Input file downloaded.", "success");
    } catch (error) {
      const message = await extractErrorMessage(
        error,
        "Failed to download input file."
      );
      addToast(message, "error");
    } finally {
      setDownloadingInputJobId(null);
    }
  };

  const handleRestart = async (job, scheduledAtLocal) => {
    if (!job?.job_id) return;
    let scheduledIso = null;
    if (scheduledAtLocal) {
      const target = new Date(scheduledAtLocal);
      if (Number.isNaN(target.getTime())) {
        addToast("Scheduled date/time is not valid.", "error");
        return;
      }
      if (target.getTime() <= Date.now() + 30 * 1000) {
        addToast(
          "Scheduled date/time must be at least 30 seconds in the future.",
          "error"
        );
        return;
      }
      scheduledIso = target.toISOString();
    }
    setRestartingJobId(job.job_id);
    try {
      await flaskApi.post(
        `${ENDPOINT}/jobs/${job.job_id}/restart`,
        scheduledIso ? { scheduled_at: scheduledIso } : {}
      );
      addToast(
        scheduledIso
          ? `Create-products job ${job.job_id} rescheduled for ${formatScheduledDisplay(
              scheduledIso
            )}.`
          : `Create-products job ${job.job_id} restarted.`,
        scheduledIso ? "info" : "success"
      );
      if (page !== 1) await setPage(1);
      else await refetchJobs();
    } catch (error) {
      const message = await extractErrorMessage(error, "Failed to restart job.");
      addToast(message, "error");
    } finally {
      setRestartingJobId(null);
    }
  };

  return (
    <>
      <ToolPageLayout
        splitLayout
        title="Create Products"
        subtitle={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
        tips={guideTips}
        onOpenInfo={() => setShowInfoModal(true)}
      >
        <form onSubmit={handleSubmit} className="tool-form">
          <div className="tool-form-main">
            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Product Sheet</h3>
              </div>
              <ToolFileUploadField
                label="Product sheet"
                file={inputFile}
                setFile={setInputFile}
                accept=".csv,.xlsx,.xls"
                helperText="CSV or XLSX in Matrixify Products format — one row per variant, grouped by Handle. Needs at least Handle, Title, Variant SKU and Variant Price. The SKU Builder output works as-is."
              />
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">
                  Tab Name{" "}
                  <span className="tool-section-optional">(optional)</span>
                </h3>
              </div>
              <div className="tool-field-stack">
                <label className="tool-label">Excel tab to read</label>
                <p className="tool-helper-text">
                  Only used for .xlsx / .xls files with multiple tabs. Leave blank
                  to use the first tab. Case-sensitive.
                </p>
                <input
                  type="text"
                  className="tool-input"
                  placeholder="e.g. Product Sheet"
                  value={sheetName}
                  onChange={(event) => setSheetName(event.target.value)}
                  disabled={uploading}
                />
              </div>
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Sales Channels</h3>
              </div>
              <div className="tool-field-stack">
                {channelsLoading ? (
                  <p className="tool-helper-text">Loading sales channels…</p>
                ) : channelsError ? (
                  <>
                    <div className="tool-job-error">{channelsError}</div>
                    <button
                      type="button"
                      className="tool-btn-secondary"
                      onClick={loadChannels}
                    >
                      Retry
                    </button>
                  </>
                ) : (
                  <>
                    <p className="tool-helper-text">
                      Every product created by this run goes on the channels ticked
                      here. Point of Sale is pre-selected, matching what the
                      Matrixify import does today. The Online Store is handled per
                      row by the sheet's own Published column, so you normally
                      leave it unticked here.
                    </p>
                    <div className="shopify-checkbox-row">
                      {channels.map((channel) => (
                        <label
                          key={channel.id}
                          className="shopify-checkbox-label"
                        >
                          <input
                            type="checkbox"
                            checked={selectedChannels.includes(channel.id)}
                            onChange={() => toggleChannel(channel.id)}
                            disabled={uploading}
                          />
                          {channel.name}
                        </label>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Default Status</h3>
              </div>
              <div className="tool-field-stack">
                <label className="tool-label" htmlFor="create-products-status">
                  Status for rows with a blank Status column
                </label>
                <select
                  id="create-products-status"
                  className="tool-input"
                  value={defaultStatus}
                  onChange={(event) => setDefaultStatus(event.target.value)}
                  disabled={uploading}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <span className="tool-helper-text">
                  {STATUS_HELPER[defaultStatus]} A row that has its own Status
                  value always wins.
                </span>
              </div>
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Safety</h3>
              </div>
              <div className="tool-field-stack">
                <label className="shopify-checkbox-label">
                  <input
                    type="checkbox"
                    checked={dryRun}
                    onChange={(event) => setDryRun(event.target.checked)}
                    disabled={uploading}
                  />
                  Dry run — validate everything, create nothing
                </label>
                <span className="tool-helper-text">
                  {dryRun
                    ? "Safe. Runs the full validation pass, including whether each handle and SKU is already taken in Shopify, so a clean dry run means the live run will work."
                    : "LIVE — products are created in Shopify. They have to be removed by hand in the Shopify admin."}
                </span>
                <label className="shopify-checkbox-label">
                  <input
                    type="checkbox"
                    checked={abortOnError}
                    onChange={(event) => setAbortOnError(event.target.checked)}
                    disabled={uploading}
                  />
                  Stop if any row has an error
                </label>
                <span className="tool-helper-text">
                  {abortOnError
                    ? "Nothing is created unless the whole file is clean. The job fails with a count and you fix the sheet before re-running."
                    : "Default. A row with an error blocks only its own product; the rest of the file is still created. Every problem is listed on the report's Issues sheet."}
                </span>
              </div>
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">When to Run</h3>
              </div>
              <div className="tool-field-stack">
                <div
                  className="tool-radio-group"
                  role="radiogroup"
                  aria-label="When to run"
                >
                  <label className="tool-radio-option">
                    <input
                      type="radio"
                      name="create-products-run-time"
                      value={RUN_NOW}
                      checked={runTime === RUN_NOW}
                      onChange={() => setRunTime(RUN_NOW)}
                      disabled={uploading}
                    />
                    <span>Run now</span>
                  </label>
                  <label className="tool-radio-option">
                    <input
                      type="radio"
                      name="create-products-run-time"
                      value={RUN_SCHEDULE}
                      checked={runTime === RUN_SCHEDULE}
                      onChange={() => setRunTime(RUN_SCHEDULE)}
                      disabled={uploading}
                    />
                    <span>Schedule for later</span>
                  </label>
                </div>
                {runTime === RUN_SCHEDULE ? (
                  <>
                    <label
                      className="tool-label"
                      htmlFor="create-products-scheduled-at"
                    >
                      Date &amp; time
                    </label>
                    <input
                      id="create-products-scheduled-at"
                      type="datetime-local"
                      className="tool-input"
                      value={scheduledAt}
                      min={minScheduledAt}
                      onChange={(event) => setScheduledAt(event.target.value)}
                      disabled={uploading}
                      required
                    />
                    <span className="tool-helper-text">
                      Uses your browser's local time. The file uploads now and the
                      job fires at the scheduled time — handy for building a season
                      ahead of the day it goes on sale.
                    </span>
                  </>
                ) : null}
              </div>
            </section>

            <div className="tool-action-stack">
              <button
                type="submit"
                className="tool-btn-primary"
                disabled={uploading || channelsLoading}
              >
                {uploading
                  ? "Uploading…"
                  : `${runTime === RUN_SCHEDULE ? "Schedule" : "Start"} ${
                      dryRun ? "Dry Run" : "Live Create"
                    }`}
              </button>
              <ToolProgressPanel
                loading={uploading}
                uploadProgress={uploadProgress}
                uploadMessage="Uploading file…"
                processingMessage="Queueing job…"
              />
            </div>
          </div>
        </form>

        <ToolJobsSection
          title="Your Create Products Jobs"
          emptyText="No create-products jobs yet. Submit a product sheet above to start one."
          jobs={jobs}
          page={page}
          totalPages={totalPages}
          totalFinished={totalFinished}
          onPageChange={setPage}
          renderJob={(j) => {
            const progress = j.progress || {};
            const total = Number(progress.total) || 0;
            const processed = Number(progress.processed) || 0;
            const pct =
              total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
            // dry_run may be absent on an in-flight doc; progress carries it too.
            const jobDryRun =
              j.dry_run !== undefined ? j.dry_run : progress.dry_run;
            return (
              <div key={j.job_id} className="tool-job-status">
                <div className="tool-job-status-row">
                  <span className="tool-job-label">Job ID</span>
                  <span className="tool-job-value">{j.job_id}</span>
                </div>
                <div className="tool-job-status-row">
                  <span className="tool-job-label">Mode</span>
                  <span className="tool-job-value">
                    {jobDryRun === false
                      ? "Create (LIVE)"
                      : jobDryRun
                      ? "Create (dry run)"
                      : "Create"}
                  </span>
                </div>
                {j.default_status ? (
                  <div className="tool-job-status-row">
                    <span className="tool-job-label">Default status</span>
                    <span className="tool-job-value">{j.default_status}</span>
                  </div>
                ) : null}
                <div className="tool-job-status-row">
                  <span className="tool-job-label">Channels</span>
                  <span className="tool-job-value">
                    {j.channel_names || "—"}
                  </span>
                </div>
                <div className="tool-job-status-row">
                  <span className="tool-job-label">Input</span>
                  <span className="tool-job-value">{j.input_filename || "—"}</span>
                </div>
                {j.sheet_name ? (
                  <div className="tool-job-status-row">
                    <span className="tool-job-label">Tab</span>
                    <span className="tool-job-value">{j.sheet_name}</span>
                  </div>
                ) : null}
                {j.abort_on_error ? (
                  <div className="tool-job-status-row">
                    <span className="tool-job-label">On error</span>
                    <span className="tool-job-value">Stop the whole run</span>
                  </div>
                ) : null}
                {j.scheduled_at ? (
                  <div className="tool-job-status-row">
                    <span className="tool-job-label">Scheduled</span>
                    <span className="tool-job-value">
                      {formatScheduledDisplay(j.scheduled_at)}
                    </span>
                  </div>
                ) : null}
                <div className="tool-job-status-row">
                  <span className="tool-job-label">Status</span>
                  <span className={`tool-job-value tool-job-status-${j.status}`}>
                    {j.status}
                  </span>
                </div>
                {total > 0 ? (
                  <>
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Progress</span>
                      <span className="tool-job-value">
                        {processed.toLocaleString()} / {total.toLocaleString()} (
                        {pct}%) products
                      </span>
                    </div>
                    <div className="progress-bar-container">
                      <div
                        className="progress-bar-fill"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </>
                ) : null}
                <div className="tool-job-counts">
                  <span>Rows: {progress.input_rows || 0}</span>
                  <span>Created: {progress.created || 0}</span>
                  <span>Would create: {progress.planned || 0}</span>
                  <span>Blocked/failed: {progress.failed || 0}</span>
                  <span>Validation errors: {progress.validation_errors || 0}</span>
                </div>
                {j.status === STATUS_FAILED && j.error ? (
                  <div className="tool-job-error">Error: {j.error}</div>
                ) : null}
                {j.status === STATUS_DONE ? (
                  <button
                    type="button"
                    className="tool-btn-primary"
                    onClick={() => handleDownload(j)}
                    disabled={downloadingJobId === j.job_id}
                  >
                    {downloadingJobId === j.job_id
                      ? "Downloading…"
                      : "Download Report"}
                  </button>
                ) : null}
                {j.status === STATUS_SCHEDULED ? (
                  <button
                    type="button"
                    className="tool-btn-secondary"
                    onClick={() => handleCancel(j)}
                    disabled={cancellingJobId === j.job_id}
                  >
                    {cancellingJobId === j.job_id ? "Cancelling…" : "Cancel"}
                  </button>
                ) : null}
                <JobInputActions
                  job={j}
                  onDownloadInput={handleDownloadInput}
                  downloadingInput={downloadingInputJobId === j.job_id}
                  onRestart={handleRestart}
                  restarting={restartingJobId === j.job_id}
                  minScheduledAt={minScheduledAt}
                />
              </div>
            );
          }}
        />
      </ToolPageLayout>

      <ToolConfirmModal
        open={Boolean(pendingLiveRun)}
        title="Live run — create products in Shopify?"
        confirmLabel="Create Products"
        cancelLabel="Cancel"
        onCancel={() => setPendingLiveRun(null)}
        onConfirm={confirmLiveRun}
      >
        <div className="tool-job-status">
          <div className="tool-job-status-row">
            <span className="tool-job-label">File</span>
            <span className="tool-job-value">{inputFile?.name || "—"}</span>
          </div>
          <div className="tool-job-status-row">
            <span className="tool-job-label">New products</span>
            <span className="tool-job-value">
              {defaultStatus} (where the sheet's Status is blank)
            </span>
          </div>
          <div className="tool-job-status-row">
            <span className="tool-job-label">Published to</span>
            <span className="tool-job-value">
              {selectedChannelNames.length
                ? selectedChannelNames.join(", ")
                : "no extra channels"}
            </span>
          </div>
          {pendingLiveRun?.scheduledIso ? (
            <div className="tool-job-status-row">
              <span className="tool-job-label">Scheduled</span>
              <span className="tool-job-value">
                {formatScheduledDisplay(pendingLiveRun.scheduledIso)}
              </span>
            </div>
          ) : null}
        </div>
        <p className="tool-helper-text">
          Products created this way have to be removed by hand in the Shopify
          admin. Run a dry run first if you have not already.
        </p>
      </ToolConfirmModal>

      <ToolInfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        toolName={toolInfo.toolName}
        description={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
      />
    </>
  );
}


export default CreateProductsTool;
